import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import EventModal from './EventModal';
import EventModalContent from './EventModalContent';
import MonthGroupView from './MonthGroupView'; // Importamos o componente separado

import cx from 'classnames';
import moment from 'moment';
import { Container, Header, Message } from 'semantic-ui-react';

moment.locale('pt-br');

const messages = defineMessages({
  loadingEvents: { id: 'Loading events…', defaultMessage: 'Loading events…' },
  errorLoadingEvents: {
    id: 'There was an error loading the events. Please try again later.',
    defaultMessage:
      'There was an error loading the events. Please try again later.',
  },
  Month: { id: 'Month', defaultMessage: 'Month' },
  Today: { id: 'Today', defaultMessage: 'Today' },
  List: { id: 'List', defaultMessage: 'List' },
  noEventsInRange: {
    id: 'There are no events in this range',
    defaultMessage: 'There are no events in this range',
  },
  downloadCalendar: {
    id: 'Download Calendar',
    defaultMessage: 'Download Calendar',
  },
  calendarTitle: {
    id: 'Calendar',
    defaultMessage: 'Calendar',
  },
  noEventsFound: {
    id: 'NoEventsFound',
    defaultMessage: 'Nenhum evento encontrado.',
  },
  loading: {
    id: 'Loading',
    defaultMessage: 'Carregando eventos...',
  },
  crossingEventsLegend: {
    id: 'Events spanning multiple months',
    defaultMessage: 'Eventos abrangendo vários meses',
  },
  allEvents: {
    id: 'All events',
    defaultMessage: 'All events',
  },
  addEvent: {
    id: 'AddEvent',
    defaultMessage: 'Add event',
  },
});

// Função para formatar eventos do Plone para um formato padronizado
const formatPloneEvents = (events) => {
  if (!events || !Array.isArray(events)) return [];

  return events.map((event) => {
    return {
      ...event,
      id: event['@id'] || event.id || '',
      title: event.title || '',
      start: event.start || '',
      end: event.end || event.start || '',
      description: event.description || '',
      location: event.location || '',
      display: 'block',
      // Adicionamos propriedades úteis para o FullCalendar
      url: event['@id'] || event.id || '',
      event_url: event.event_url || '',
      allDay: true,
    };
  });
};

// Função para ajustar o campo 'end' apenas para o FullCalendar (exclusivo)
const adjustEventsForFullCalendar = (events) => {
  if (!events || !Array.isArray(events)) return [];
  return events.map((event) => {
    let endDate = event.end || event.start || '';
    if (endDate) {
      const end = new Date(endDate);
      if (event.end && event.end !== event.start) {
        end.setDate(end.getDate() + 1);
        endDate = end.toISOString().slice(0, 10);
      }
    }
    return {
      ...event,
      end: endDate,
    };
  });
};

const Body = (props) => {
  const { data = {}, id, path, intl, className } = props;
  const lang = useSelector((state) => state.intl.locale);
  const [eventsFiltered, setEventsFiltered] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentView, setCurrentView] = useState('dayGridMonth');

  const dispatch = useDispatch();
  const subrequestID = `calendar-events-${id}`;
  const currentPath = path || window.location.pathname;
  const initialPath = getBaseUrl(currentPath);

  // Configurações do bloco
  const title = data.title || intl.formatMessage(messages.calendarTitle);

  // Função para formatar o título do calendário sem a preposição "de"
  const formatCalendarTitle = useCallback(
    (arg) => {
      if (currentView === 'monthGrouped') {
        return intl.formatMessage(messages.allEvents);
      }

      // Extraímos o ano e o mês diretamente do objeto DateFormattingArg
      const year = arg.date.year;

      // Os meses no FullCalendar são baseados em zero (0-11), como no JavaScript
      // Incrementamos em 1 para passar ao toLocaleString
      const monthIndex = arg.date.month;

      // Cria um objeto Date temporário para usar com toLocaleString
      const tempDate = new Date(year, monthIndex, 1);
      const month = tempDate.toLocaleString(lang, { month: 'long' });

      // Primeira letra maiúscula e resto em minúsculas (capitalize)
      const capitalizedMonth =
        month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();

      // Retorna no formato "Maio 2025" (sem a preposição "de")
      return `${capitalizedMonth} ${year}`;
    },
    [lang, currentView, intl],
  );

  // Função para agrupar eventos por mês
  const groupEventsByMonth = useCallback((eventsList) => {
    if (!eventsList?.length) return [];

    const grouped = {};

    eventsList.forEach((event) => {
      if (event.start) {
        const startDate = new Date(event.start);
        const year = startDate.getFullYear();
        const month = startDate.getMonth();

        // Chave para agrupar: "ano-mês"
        const key = `${year}-${month}`;

        if (!grouped[key]) {
          // Criamos um novo objeto Date para o primeiro dia do mês
          const monthDate = new Date(year, month, 1);
          grouped[key] = {
            date: monthDate,
            events: [],
          };
        }

        grouped[key].events.push(event);
      }
    });

    // Convertemos o objeto agrupado para um array ordenado por data
    return Object.values(grouped).sort((a, b) => a.date - b.date);
  }, []);

  // Função para formatar o nome do mês e ano para o título do grupo
  const formatMonthGroupTitle = useCallback(
    (date) => {
      const year = date.getFullYear();
      const month = date.toLocaleString(lang, { month: 'long' });
      const capitalizedMonth =
        month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();

      return `${capitalizedMonth} ${year}`;
    },
    [lang],
  );

  // Consulta para obter apenas eventos
  const eventQuery = React.useMemo(
    () => ({
      // metadata_fields: '_all',
      metadata_fields: [
        'title',
        'description',
        'start',
        'end',
        'event_url',
        'location',
        'theme',
        'organizer',
      ],
      b_size: 1000, // Um valor grande para obter todos os eventos
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.is',
          v: ['Event'],
        },
      ],
      sort_on: 'start',
      sort_order: 'ascending',
    }),
    [],
  ); // Empty dependency array to ensure this object doesn't change on re-renders

  // Obter resultados da consulta do estado
  const querystringResults = useSelector(
    (state) => state.querystringsearch.subrequests,
  );

  const loading = querystringResults?.[subrequestID]?.loading || false;
  const loaded = querystringResults?.[subrequestID]?.loaded || false;
  const events = React.useMemo(
    () => querystringResults?.[subrequestID]?.items || [],
    [querystringResults, subrequestID],
  );

  // Disparar a consulta quando o componente montar
  useEffect(() => {
    dispatch(getQueryStringResults(initialPath, eventQuery, subrequestID));
  }, [dispatch, initialPath, subrequestID, eventQuery]);

  // Definir todos os eventos (sem filtros) e formatá-los
  useEffect(() => {
    if (events && events.length > 0) {
      const formattedEvents = formatPloneEvents(events);
      setEventsFiltered(formattedEvents);
    } else {
      setEventsFiltered([]);
    }
  }, [events]);

  const handleEventClick = (info) => {
    // Busca o evento original pelo id
    const eventId = info.event.id;
    const originalEvent = eventsFiltered.find((e) => e.id === eventId);
    if (!originalEvent) return;
    setSelectedEvent(originalEvent);
    setModalOpen(true);
    info.jsEvent.preventDefault();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <div className={cx('calendar-block', className)}>
      <Container>
        <Header
          as="h2"
          className="calendar-title"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>{title}</span>
          {useSelector((state) => !!state.userSession?.token) && (
            <a
              href={`/${toBackendLang(lang)}/assets/eventos-do-calendario/add?type=Event`}
              className="ui button primary"
              style={{ marginLeft: 16, display: 'flex', alignItems: 'center' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="plus icon" style={{ marginRight: 6 }}></i>
              {intl.formatMessage(messages.addEvent)}
            </a>
          )}
        </Header>

        {loading && !loaded && (
          <Message info className="loading-events">
            {intl.formatMessage(messages.loading)}
          </Message>
        )}

        {loaded && eventsFiltered.length === 0 && (
          <Message warning className="no-events">
            {intl.formatMessage(messages.noEventsFound)}
          </Message>
        )}

        {loaded && eventsFiltered.length > 0 && (
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            locale={toBackendLang(lang)}
            events={adjustEventsForFullCalendar(eventsFiltered)}
            contentHeight="auto"
            eventDidMount={(info) => {
              if (info.event && info.event.id && info.el) {
                info.el.setAttribute('data-event-id', info.event.id);
              }
            }}
            view={currentView}
            viewDidMount={(arg) => {
              setCurrentView(arg.view.type);
            }}
            datesSet={(arg) => {
              if (arg && arg.view && arg.view.type !== currentView) {
                setCurrentView(arg.view.type);
              }
            }}
            headerToolbar={
              currentView === 'monthGrouped'
                ? {
                    left: 'dayGridMonth,monthGrouped',
                    center: 'title',
                    right: '',
                  }
                : {
                    left: 'dayGridMonth,monthGrouped',
                    center: 'prev title next',
                    right: 'today',
                  }
            }
            // Visualizações personalizadas
            views={{
              monthGrouped: {
                // Usamos um componente personalizado para esta visualização
                content: () => {
                  // Renderizamos o nosso componente personalizado
                  return (
                    <MonthGroupView
                      events={events}
                      groupEventsByMonth={groupEventsByMonth}
                      formatMonthGroupTitle={formatMonthGroupTitle}
                      lang={lang}
                      intl={intl}
                      messages={messages}
                    />
                  );
                },
                buttonText: intl.formatMessage(messages.List),
                // Desabilitamos a navegação padrão, pois todos os eventos serão exibidos
                display: 'custom',
                duration: { months: 12 },
              },
            }}
            // Usando função personalizada para formatar o título do calendário
            titleFormat={formatCalendarTitle}
            buttonText={{
              today: intl.formatMessage(messages.Today),
              month: intl.formatMessage(messages.Month),
            }}
            displayEventTime={false}
            eventOrder="priority"
            eventOrderStrict={true}
            eventSortComparator={(a, b) => {
              // Função comparadora personalizada para ordenação dupla
              const priorityA = a.priority || 99;
              const priorityB = b.priority || 99;

              // Se as prioridades são diferentes, ordenamos por prioridade
              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }

              // Se as prioridades são iguais, ordenamos pela duração em dias (do maior para o menor)
              // Extraímos a duração de cada evento, ou usamos 1 como padrão
              const durationInDaysA = a.durationInDays || 1;
              const durationInDaysB = b.durationInDays || 1;
              return durationInDaysB - durationInDaysA; // Ordem decrescente (do maior para o menor)
            }}
            noEventsContent={intl.formatMessage(messages.noEventsInRange)}
            eventClick={handleEventClick}
            eventMouseEnter={(info) => {
              // Pegar ID único do evento
              const eventId = info.event.id;

              // Aplicar classe de destaque a todos os elementos com o mesmo ID de evento
              document
                .querySelectorAll(`.fc-event[data-event-id="${eventId}"]`)
                .forEach((el) => {
                  el.classList.add('fc-event-highlighted');
                });
            }}
            eventMouseLeave={(info) => {
              // Pegar ID único do evento
              const eventId = info.event.id;

              // Remover classe de destaque de todos os elementos com o mesmo ID
              document
                .querySelectorAll(`.fc-event[data-event-id="${eventId}"]`)
                .forEach((el) => {
                  el.classList.remove('fc-event-highlighted');
                });
            }}
          />
        )}
        <EventModal open={modalOpen} onClose={handleModalClose}>
          <EventModalContent event={selectedEvent} lang={lang} />
        </EventModal>
      </Container>
    </div>
  );
};

Body.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  id: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string,
};

export default injectIntl(Body);
